/**
 * AI Service - proxied through backend Lambda to avoid CORS issues.
 * The backend calls Hugging Face Inference API server-side.
 */

const getApiBaseUrl = () => {
  const url = (import.meta.env.VITE_API_BASE_URL || '').trim();
  return url.replace(/\/+$/, '');
};

const getToken = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u?.tokens?.idToken || u?.idToken || '';
  } catch {
    return '';
  }
};

/**
 * Call the backend AI proxy endpoint.
 * The backend forwards the request to Hugging Face server-side.
 */
const callAiProxy = async (prompt, options = {}) => {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    throw new Error('API base URL is not configured.');
  }

  const token = getToken();
  if (!token) {
    throw new Error('You must be logged in to use AI features.');
  }

  const {
    maxNewTokens = 1024,
    temperature = 0.7
  } = options;

  const response = await fetch(`${baseUrl}/me/ai/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      prompt,
      max_new_tokens: maxNewTokens,
      temperature
    })
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || `AI request failed with status ${response.status}`);
  }

  const data = await response.json();
  return data.generated_text || '';
};

// ─────────────── Prompt Templates ───────────────

/**
 * Generate a cover letter from resume data.
 * @param {Object} resume - The resume object
 * @param {string} [jobDescription] - Optional target job description
 * @returns {Promise<string>} Generated cover letter text
 */
export const generateCoverLetter = async (resume, jobDescription = '') => {
  const name = resume.title || resume.name || 'the applicant';
  const headline = resume.headline || '';
  const summary = resume.professional_summary || resume.summary || '';
  const experienceText = (resume.experience || [])
    .slice(0, 3)
    .map((exp) => `${exp.title || ''} at ${exp.company || ''}: ${(exp.description || '').slice(0, 150)}`)
    .join('\n');
  const skillsText = [
    ...(resume.skills || []),
    ...(resume.core_skills || []).map((s) => (typeof s === 'string' ? s : s.name))
  ].slice(0, 15).join(', ');

  const jobContext = jobDescription
    ? `\nThe target job description is:\n${jobDescription.slice(0, 500)}\n`
    : '';

  const prompt = `You are a professional resume writer. Write a polished, professional cover letter for the following person. The letter should be 3-4 paragraphs, formal but personable, and highlight their key strengths.${jobContext}

Candidate name: ${name}
Headline: ${headline}
Summary: ${summary}
Key experience:
${experienceText}
Skills: ${skillsText}

Write ONLY the cover letter text. Do not include explanations or commentary.`;

  const result = await callAiProxy(prompt, { maxNewTokens: 800, temperature: 0.7 });
  return result.trim();
};

/**
 * Rewrite an experience bullet point to be more professional and impactful.
 * @param {string} bulletText - The original bullet/description text
 * @param {string} [jobTitle] - The job title for context
 * @returns {Promise<string>} Rewritten bullet text
 */
export const rewriteExperienceBullet = async (bulletText, jobTitle = '') => {
  const titleContext = jobTitle ? ` for the role of "${jobTitle}"` : '';

  const prompt = `You are an expert resume writer. Rewrite the following resume experience description${titleContext} to be more professional, concise, and impactful. Use strong action verbs and quantify achievements where possible. Keep the same meaning but improve the language.

Original text:
${bulletText.slice(0, 500)}

Write ONLY the improved text. Do not include explanations.`;

  const result = await callAiProxy(prompt, { maxNewTokens: 400, temperature: 0.6 });
  return result.trim();
};

/**
 * Suggest relevant skills based on job title and experience.
 * @param {Object} resume - The resume object
 * @returns {Promise<string[]>} Array of suggested skill names
 */
export const suggestSkills = async (resume) => {
  const headline = resume.headline || resume.target_role || '';
  const experienceText = (resume.experience || [])
    .slice(0, 3)
    .map((exp) => `${exp.title || ''} at ${exp.company || ''}`)
    .join(', ');
  const existingSkills = [
    ...(resume.skills || []),
    ...(resume.core_skills || []).map((s) => (typeof s === 'string' ? s : s.name))
  ].join(', ');

  const prompt = `You are a career advisor. Based on the following professional profile, suggest 10 relevant skills that would strengthen this resume. Do NOT repeat skills they already have.

Job title/headline: ${headline}
Experience: ${experienceText}
Existing skills: ${existingSkills}

Return ONLY a numbered list of 10 skills, one per line, like:
1. Skill Name
2. Skill Name
...
Do not include explanations.`;

  const result = await callAiProxy(prompt, { maxNewTokens: 300, temperature: 0.7 });

  // Parse the numbered list
  const skills = result
    .split('\n')
    .map((line) => line.replace(/^\d+[.)]\s*/, '').trim())
    .filter((line) => line.length > 0 && line.length < 60);

  return skills.slice(0, 10);
};

// ─────────────── Portfolio AI Functions ───────────────

/**
 * Summarize a document's text content using AI.
 * @param {string} documentText - Extracted plain text from the document
 * @param {string} [fileName] - Original filename for context
 * @returns {Promise<string>} 1-2 paragraph summary
 */
export const summarizeDocument = async (documentText, fileName = '') => {
  if (!documentText || documentText.trim().length < 20) {
    throw new Error('Document text is too short to summarize.');
  }

  const fileContext = fileName ? `\nDocument filename: ${fileName}` : '';
  const prompt = `You are a professional document analyst. Summarize the following professional document in 1-2 paragraphs. Highlight the author's key contributions, skills demonstrated, and the document's purpose. Be specific and reference actual content from the text.${fileContext}

Document content:
${documentText.slice(0, 3000)}

Write ONLY the summary paragraphs. Do not include explanations or commentary.`;

  const result = await callAiProxy(prompt, { maxNewTokens: 400, temperature: 0.5 });
  return result.trim();
};

/**
 * Generate resume bullet points from a performance evaluation or review.
 * @param {string} documentText - Extracted text from the evaluation document
 * @param {string} [jobTitle] - Optional job title for context
 * @returns {Promise<string[]>} Array of resume-ready bullet strings
 */
export const generateBulletsFromEvaluation = async (documentText, jobTitle = '') => {
  if (!documentText || documentText.trim().length < 20) {
    throw new Error('Document text is too short to generate bullets.');
  }

  const titleContext = jobTitle ? `\nThe person's job title is: ${jobTitle}` : '';
  const prompt = `You are an expert resume writer. Based on the following performance evaluation or professional document, generate 5-8 resume-ready bullet points. Use the STAR method (Situation, Task, Action, Result) where possible. Start each bullet with a strong action verb. Quantify achievements where the data supports it.${titleContext}

Document content:
${documentText.slice(0, 3000)}

Return ONLY a numbered list of bullet points, one per line, like:
1. Led a team of 12 engineers to deliver a mission-critical platform migration, reducing downtime by 40%
2. ...
Do not include explanations.`;

  const result = await callAiProxy(prompt, { maxNewTokens: 600, temperature: 0.7 });

  // Parse numbered list
  const bullets = result
    .split('\n')
    .map((line) => line.replace(/^\d+[.)]\s*/, '').trim())
    .filter((line) => line.length > 10 && line.length < 500);

  return bullets.slice(0, 8);
};

/**
 * Extract skills from a document and compare against existing skills.
 * @param {string} documentText - Extracted text from the document
 * @param {string[]} [existingSkills] - Skills the user already has
 * @returns {Promise<string[]>} Array of new skill names found
 */
export const extractSkillsFromDocument = async (documentText, existingSkills = []) => {
  if (!documentText || documentText.trim().length < 20) {
    throw new Error('Document text is too short to extract skills.');
  }

  const existingList = existingSkills.length > 0
    ? `\nThe person already has these skills (do NOT repeat them): ${existingSkills.join(', ')}`
    : '';

  const prompt = `You are a career advisor. Analyze the following professional document and extract all technical skills, soft skills, and professional competencies demonstrated in it. Only return skills that are clearly evidenced in the text.${existingList}

Document content:
${documentText.slice(0, 3000)}

Return ONLY a numbered list of skills, one per line, like:
1. Project Management
2. Python
3. Data Analysis
...
Do not include explanations. List up to 15 skills.`;

  const result = await callAiProxy(prompt, { maxNewTokens: 300, temperature: 0.5 });

  const skills = result
    .split('\n')
    .map((line) => line.replace(/^\d+[.)]\s*/, '').trim())
    .filter((line) => line.length > 1 && line.length < 60);

  // De-duplicate against existing skills (case-insensitive)
  const existingLower = existingSkills.map((s) => s.toLowerCase());
  return skills
    .filter((s) => !existingLower.includes(s.toLowerCase()))
    .slice(0, 15);
};

/**
 * Score a portfolio and provide improvement suggestions.
 * @param {Object} portfolioData
 * @param {Array} portfolioData.files - Portfolio file metadata array
 * @param {Object} portfolioData.resume - Resume data (skills, experience summary)
 * @returns {Promise<{score: number, strengths: string[], suggestions: string[]}>}
 */
export const scorePortfolio = async (portfolioData) => {
  const { files = [], resume = {} } = portfolioData;

  const categoryCounts = files.reduce((acc, f) => {
    acc[f.category] = (acc[f.category] || 0) + 1;
    return acc;
  }, {});

  const categoryBreakdown = Object.entries(categoryCounts)
    .map(([cat, count]) => `${cat}: ${count} files`)
    .join(', ');

  const skillsList = [
    ...(resume.skills || []),
    ...(resume.core_skills || []).map((s) => (typeof s === 'string' ? s : s.name))
  ].slice(0, 20).join(', ');

  const experienceSummary = (resume.experience || [])
    .slice(0, 3)
    .map((exp) => `${exp.title || 'Role'} at ${exp.company || 'Company'}`)
    .join('; ');

  const summaries = files
    .filter((f) => f.summary)
    .slice(0, 5)
    .map((f) => `- ${f.name}: ${f.summary.slice(0, 100)}`)
    .join('\n');

  const prompt = `You are a career advisor evaluating a professional portfolio. Rate this portfolio on a scale of 1-100 and provide feedback.

Portfolio overview:
- Total files: ${files.length}
- Categories: ${categoryBreakdown || 'none'}
- File summaries:
${summaries || '(no summaries available)'}

Person's profile:
- Skills: ${skillsList || 'none listed'}
- Experience: ${experienceSummary || 'none listed'}

Respond in EXACTLY this format (no other text):
SCORE: [number]
STRENGTHS:
- [strength 1]
- [strength 2]
- [strength 3]
SUGGESTIONS:
- [suggestion 1]
- [suggestion 2]
- [suggestion 3]
- [suggestion 4]
- [suggestion 5]`;

  const result = await callAiProxy(prompt, { maxNewTokens: 500, temperature: 0.6 });

  // Parse structured response
  let score = 50;
  const strengths = [];
  const suggestions = [];

  const scoreMatch = result.match(/SCORE:\s*(\d+)/i);
  if (scoreMatch) score = Math.min(100, Math.max(0, parseInt(scoreMatch[1], 10)));

  let currentSection = '';
  for (const line of result.split('\n')) {
    const trimmed = line.trim();
    if (/^STRENGTHS:/i.test(trimmed)) {
      currentSection = 'strengths';
    } else if (/^SUGGESTIONS:/i.test(trimmed)) {
      currentSection = 'suggestions';
    } else if (trimmed.startsWith('-') || trimmed.startsWith('*')) {
      const text = trimmed.replace(/^[-*]\s*/, '').trim();
      if (text && currentSection === 'strengths') strengths.push(text);
      if (text && currentSection === 'suggestions') suggestions.push(text);
    }
  }

  return {
    score,
    strengths: strengths.slice(0, 5),
    suggestions: suggestions.slice(0, 5)
  };
};

/**
 * Suggest the best portfolio category for a file based on its content.
 * @param {string} documentText - Extracted text from the document
 * @param {string} fileName - Original filename for context
 * @returns {Promise<string>} A category ID from the valid list, or 'other'
 */
export const suggestFileCategory = async (documentText, fileName = '') => {
  const VALID_CATEGORIES = [
    'word', 'excel', 'powerpoint', 'images', 'videos', 'awards',
    'certificates', 'projects', 'leadership', 'case_studies',
    'community_service', 'performance_evaluations', 'publications',
    'training', 'reports', 'other'
  ];

  if (!documentText || documentText.trim().length < 20) {
    return 'other';
  }

  const prompt = `You are a document classification expert. Based on the following document content, classify it into EXACTLY ONE of these portfolio categories:

- word: General word processing documents
- awards: Award letters, recognition documents, honors
- certificates: Certifications, licenses, credentials
- projects: Project plans, deliverables, technical projects
- leadership: Leadership development, team management, mentoring, coaching, supervision documents
- case_studies: Case studies, research scenarios, analytical reports
- community_service: Community service, volunteering, outreach, civic engagement
- performance_evaluations: Performance reviews, evaluations, appraisals, ratings, assessments
- publications: Published papers, articles, journals
- training: Training materials, workshop documents, course completion
- reports: Reports, analysis documents, data summaries
- other: Anything that doesn't fit above

Filename: ${fileName}
Document excerpt:
${documentText.slice(0, 1500)}

Respond with ONLY the category ID (one word like "leadership" or "case_studies"). Nothing else.`;

  try {
    const result = await callAiProxy(prompt, { maxNewTokens: 20, temperature: 0.2 });
    const suggested = result.trim().toLowerCase().replace(/[^a-z_]/g, '');
    return VALID_CATEGORIES.includes(suggested) ? suggested : 'other';
  } catch (err) {
    console.warn('[AI Category] Failed to suggest category:', err.message);
    return 'other';
  }
};

/**
 * Check if the AI service is properly configured.
 * Now always returns true since AI calls go through the backend.
 * @returns {boolean}
 */
export const isAiConfigured = () => {
  return true;
};

export default {
  generateCoverLetter,
  rewriteExperienceBullet,
  suggestSkills,
  summarizeDocument,
  generateBulletsFromEvaluation,
  extractSkillsFromDocument,
  scorePortfolio,
  suggestFileCategory,
  isAiConfigured
};
